import { textEffectAttrs, textEffectSvgAttrs, TimelineElement } from 'ai-tutor-player';

import type { ScriptElement, VideoContent } from '@/lib/schema.types';

import { CalloutElement } from '../canvas/elements/CalloutElement';
import { HandwritingText } from '../canvas/elements/HandwritingText';
import { ChipElement } from '../canvas/elements/ChipElement';
import { EmbedElement } from '../canvas/elements/EmbedElement';
import { IconElement } from '../canvas/elements/IconElement';
import { ImageElement } from '../canvas/elements/ImageElement';
import { LatexElement } from '../canvas/elements/LatexElement';
import { McqElement } from '../canvas/elements/McqElement';
import { MermaidElement } from '../canvas/elements/MermaidElement';
import { ShapeElement } from '../canvas/elements/ShapeElement';
import { ThreeDElement } from '../canvas/elements/ThreeDElement';
import { VideoElement } from '../canvas/elements/VideoElement';

/**
 * Read-only stage version of an element. We bypass the editor wrappers
 * (drag handles, Tiptap editing, selection rings) and render just the visual.
 *
 * NOTE: intentionally NOT the player's `StageElement` — this one renders via
 * the admin's own editor element components (authoring visuals), while the
 * player's renders the classroom playback visuals. Do not "deduplicate" them.
 */
export function StageElement({
  element,
  fill,
  previewing,
  revealed = true,
  writeMs,
}: {
  element: ScriptElement;
  /** Set to true when the element should fill its parent (freeform stage). */
  fill?: boolean;
  /** During playback/preview — autoplays a video flagged `autoplay`. */
  previewing?: boolean;
  /** Whether the element is currently revealed in playback. Handwriting only
   *  starts writing once this flips true (so it doesn't run while still hidden). */
  revealed?: boolean;
  /** Target write-on duration (ms) = the line's spoken span, so the pen paces
   *  to the narration instead of a fixed speed. */
  writeMs?: number;
}) {
  switch (element.type) {
    case 'text':
      // Handwritten (default) → single-stroke writing; animates on reveal.
      // Accent effects wrap the SVG writing the same way playback does.
      if (element.handwritten !== false) {
        const svgEffects = textEffectSvgAttrs(
          element.style,
          (element.style?.fontSize as number | undefined) ?? 18,
        );
        const hand = (
          <HandwritingText
            text={plainText(typeof element.content === 'string' ? element.content : '')}
            // Match TextElement's defaults exactly so edit & teach lay out the
            // same (otherwise teach text is larger and overflows/clips).
            size={(element.style?.fontSize as number | undefined) ?? 18}
            weight={(element.style?.fontWeight as number | undefined) ?? 400}
            color={(element.style?.color as string | undefined) ?? '#1a1a2e'}
            seed={element.id}
            animate={!!previewing && revealed}
            speed={element.handwritingSpeed ?? 0.3}
            showPen={element.handwritingPen !== false}
            targetMs={writeMs}
          />
        );
        return svgEffects ? (
          <div
            {...(svgEffects['data-text-hl'] != null ? { 'data-text-hl': '' } : {})}
            style={svgEffects.style}
          >
            {hand}
          </div>
        ) : (
          hand
        );
      }
      return <StageText element={element} />;
    case 'image':
      return <ImageElement element={element} fill={fill} />;
    case 'shape':
      return <ShapeElement element={element} fillBox={fill} />;
    case 'mcq':
      return <McqElement element={element} />;
    case 'chip':
      return <ChipElement element={element} />;
    case 'video':
      return (
        <VideoElement
          element={element}
          autoPlay={previewing && !!(element.content as VideoContent | undefined)?.autoplay}
        />
      );
    case 'embed':
      return <EmbedElement element={element} isSelected={false} />;
    case '3d':
      return <ThreeDElement element={element} fill={fill} />;
    case 'latex':
      // Playback: equations write themselves on like handwriting (pencil +
      // sequential glyph reveal) the moment they're revealed.
      return <LatexElement element={element} fill={fill} writing={!!previewing && revealed} writeMs={writeMs} />;
    case 'icon':
      return <IconElement element={element} />;
    case 'callout':
      return <CalloutElement element={element} />;
    case 'mermaid':
      return <MermaidElement element={element} fill={fill} />;
    case 'timeline':
      return <TimelineElement element={element} fill={fill} />;
    default:
      return null;
  }
}

/** Rich-text HTML → plain text (with line breaks) for the handwriting renderer. */
function plainText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|h[1-6]|li)>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\n{2,}/g, '\n')
    .trim();
}

function StageText({ element }: { element: ScriptElement }) {
  const html = typeof element.content === 'string' ? element.content : '';
  const fontSize = (element.style?.fontSize as number | undefined) ?? 18;
  const effects = textEffectAttrs(element.style, fontSize);
  const hlAttr = effects['data-text-hl'] != null ? { 'data-text-hl': '' } : {};
  const style: React.CSSProperties = {
    fontSize,
    fontWeight: (element.style?.fontWeight as number | undefined) ?? 400,
    color: (element.style?.color as string | undefined) ?? '#1a1a2e',
    // Board typeface by default — matches the shared player, where text with
    // no authored family renders in the chalk font even when handwriting is
    // unticked (the flag controls the write-on animation, not the face).
    fontFamily: (element.style?.fontFamily as string | undefined) ?? 'Kalam, cursive',
    textAlign: (element.style?.textAlign as React.CSSProperties['textAlign']) ?? 'left',
    lineHeight: 1.4,
    ...effects.style,
  };
  if (html.trim().startsWith('<')) {
    return (
      <div style={style} className="w-full" {...hlAttr} dangerouslySetInnerHTML={{ __html: html }} />
    );
  }
  return (
    <div style={style} className="w-full whitespace-pre-wrap" {...hlAttr}>
      {html}
    </div>
  );
}
